import React, { useState, useCallback, useMemo } from 'react';
import dayjs from 'dayjs';
import { v4 as uuidv4 } from 'uuid';
import { TaskItem } from '@demind-inc/core';
import { AddOutlined, CloseOutlined } from '@mui/icons-material';
import { Typography, Button, Dialog, IconButton, useMediaQuery, useTheme } from '@mui/material';

import './AddTaskPage.scss';
import { OnboardingWrapper, TaskCreateCard, KanbanTaskCard } from '../../../components';
import {
  useOnboarding,
  useTodoTasksContext,
  AddTodoTaskItemProps,
  useAddTodoTask,
  useAuthContext,
  TodoTasksProvider,
  CalendarProvider,
} from '../../../data-access';
import { useTaskSettings } from '../../../hooks';
import {
  getFormattedDueDateForAddTask,
  getStartDateTimeFromDuration,
  SORT_INDEX_INCREMENT,
  getDueDateYYYYMMDD,
  getIsOverdue,
} from '../../../helpers';
import AskAIToAddTasks from './AskAIToAddTasks';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';

const today = dayjs();
const todayStr = today.format('YYYY-MM-DD');

const AddTaskPage: React.FC = () => {
  const { currentOnboardingStep, goToNextStep, goToPreviousStep, findNextStep } = useOnboarding();
  const { todoProjects, todoTasksByDate, findProject, isDateLoading } = useTodoTasksContext();
  const { user } = useAuthContext();
  const { addTodoTask } = useAddTodoTask();
  const isFinishedAiAssistantOnboarding = !!user.onboarding?.aiAssistantDemo;

  const nextStepKey = findNextStep()?.key;

  const { taskSettings } = useTaskSettings();
  const [isCreatingTask, setIsCreatingTask] = useState(false);

  const [isAIModalOpen, setIsAIModalOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Get tasks for today
  const todayTasks = useMemo(() => {
    const tasksForToday = todoTasksByDate.find((item) => item.date === todayStr);
    return tasksForToday?.tasks || [];
  }, [todoTasksByDate, todayStr]);

  // Sort tasks: incomplete first, then completed, both sorted by sortIndex
  const sortedTasks = useMemo(() => {
    const incompleteTasks = todayTasks.filter((t) => !t.completed);
    const completedTasks = todayTasks.filter((t) => t.completed);

    const sortByIndex = (tasks: TaskItem[]) => {
      return [...tasks].sort((a, b) => {
        if (a.sortIndex === undefined && b.sortIndex === undefined) return 0;
        if (a.sortIndex === undefined) return 1;
        if (b.sortIndex === undefined) return -1;
        return a.sortIndex - b.sortIndex;
      });
    };

    return [...sortByIndex(incompleteTasks), ...sortByIndex(completedTasks)];
  }, [todayTasks]);

  const isLoadingToday = isDateLoading(todayStr);

  const handleCreateTask = useCallback(
    async (task: TaskItem) => {
      setIsCreatingTask(false);
      const taskId = uuidv4().replace(/-/g, '');
      const selectedProject = todoProjects.find((p) => p.todoTaskId === task.boardId);

      const defaultDueDateTime = {
        date: task.dueDateTime?.date || today.format('YYYY-MM-DD'),
        datetime: task.dueDateTime?.datetime,
        timezone: dayjs.tz.guess(),
      };
      const defaultDuration = task.duration || {
        amount: task.duration?.amount || taskSettings.durationMin,
        unit: 'minute' as const,
      };

      const taskPayload: AddTodoTaskItemProps = {
        name: task.name,
        desc: '',
        ...getFormattedDueDateForAddTask('due', defaultDueDateTime),
        ...getStartDateTimeFromDuration(task.duration || defaultDuration, defaultDueDateTime),
        appFrom: selectedProject?.appFrom || 'lifestack',
        duration: task.duration || defaultDuration,
        addedToCalendar: false,
        completed: false,
        sortIndex: task.sortIndex,
        updatedAt: new Date().toISOString(),
        boardId: task.boardId,
      };

      await addTodoTask({
        userId: user.userId,
        newTaskInfo: taskPayload,
        boardId: task.boardId,
        taskId,
      });
    },
    [addTodoTask, user.userId, todoProjects, today, taskSettings.durationMin]
  );

  return (
    <OnboardingWrapper
      title="Add your first tasks"
      buttons={[
        {
          text: 'Next',
          onPress: goToNextStep,
          variant: 'contained',
        },
      ]}
      onBack={goToPreviousStep}
      currentOnboardingStep={currentOnboardingStep}
      contentContainerStyle={{
        display: 'flex',
        justifyContent: 'center',
      }}
      buttonContainerStyle={{ margin: 'auto', flexDirection: 'column' }}
    >
      <div className="add-task">
        <div className="add-task__schedule-card">
          <div className="add-task__schedule-header">
            <div className="add-task__schedule-date">
              <span className="add-task__schedule-weekday">{today.format('dddd')}</span>
              <span className="add-task__schedule-day">{today.format('MMM DD')}</span>
            </div>
          </div>
          <div className="add-task__kanban-container">
            {sortedTasks.length > 0 && (
              <div className="add-task__tasks-list">
                {sortedTasks.map((task) => (
                  <KanbanTaskCard
                    key={task.taskId}
                    task={task}
                    isOverDue={getIsOverdue(getDueDateYYYYMMDD(task.dueDateTime) || todayStr)}
                    projectName={findProject(task.boardId!)?.name}
                    onTaskComplete={() => void 0}
                    onTaskDelete={() => void 0}
                    onClick={() => void 0}
                    onTaskCreate={async () => Promise.resolve()}
                  />
                ))}
              </div>
            )}
            {isCreatingTask ? (
              <TaskCreateCard
                task={
                  {
                    sortIndex: SORT_INDEX_INCREMENT,
                  } as TaskItem
                }
                date={today.format('YYYY-MM-DD')}
                onSave={handleCreateTask}
                onCancel={() => setIsCreatingTask(false)}
              />
            ) : (
              <>
                <Button
                  variant="outlined"
                  startIcon={<AddOutlined />}
                  onClick={() => setIsCreatingTask(true)}
                  className="add-task__add-button"
                  fullWidth
                >
                  <Typography variant="body1" fontWeight={500}>
                    Add a task
                  </Typography>
                </Button>
                {isMobile && (
                  <Button
                    className="add-task__ai-button-mobile"
                    variant="contained"
                    onClick={() => setIsAIModalOpen(true)}
                    fullWidth
                  >
                    <Typography variant="body1" fontWeight={600}>
                      Ask AI to create tasks
                    </Typography>
                  </Button>
                )}
              </>
            )}
          </div>
        </div>

        <div className="add-task__ai-panel add-task__ai-panel--desktop">
          <AskAIToAddTasks />
        </div>
      </div>

      <Dialog
        open={isAIModalOpen}
        onClose={() => setIsAIModalOpen(false)}
        maxWidth="sm"
        fullScreen={isMobile}
        className="add-task__ai-modal"
        PaperProps={{
          className: 'add-task__ai-modal__paper',
        }}
      >
        <IconButton
          className="add-task__ai-modal__close-button"
          onClick={() => setIsAIModalOpen(false)}
          size="small"
        >
          <CloseOutlined />
        </IconButton>
        <div className="add-task__ai-panel add-task__ai-panel--mobile">
          <AskAIToAddTasks />
        </div>
      </Dialog>
    </OnboardingWrapper>
  );
};

const AddTaskPageWrapper: React.FC = () => {
  return (
    <CalendarProvider>
      <TodoTasksProvider>
        <DndProvider backend={HTML5Backend}>
          <AddTaskPage />
        </DndProvider>
      </TodoTasksProvider>
    </CalendarProvider>
  );
};

export default AddTaskPageWrapper;
